import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// Import Components
import Loader from "../../../components/Loader/Index.jsx";
import PaginationComponent from "../../../components/_utils/PaginationComponent/index.jsx";
import PrimaryButton from "../../../components/_utils/CommonButtons/PrimaryButton/index.jsx";
import CommonAccountApprovalCard from "../../../components/_utils/CommonAccountApprovalCard/index.jsx";
import TopNavComponent from "../../../components/_utils/CommonTopNav/TopNavComponent/index.jsx";
import PrivateDetailPopup from "../PrivateTxnSingleApproval/PrivateDetailPopup/index.jsx";
import CommonToast from "../../../components/_utils/CommonToast/index.jsx";
import CommonAssignLabelPopup from "../../../components/_utils/CommonPopups/CommonAssignLabelPopup/index.jsx";

// Import Redux
import {
  disableSwitcher,
  enableSwitcher,
} from "../../../redux/features/multiEntitySwitcher/multiEntitySwitcher.js";
import { getOwnerAccBalance } from "../../../redux/features/owner/ownerAllVerification/ownerAllVerification.js";
import {
  addNewLabelPvt,
  getAllPrivateTransactions,
  removeNewLabelPvt,
} from "../../../redux/features/privatetransactions/privatetransactions.js";
import { beneCategories } from "../../../redux/features/beneficiary/beneAllVerification/beneAllVerification.js";

// Import utils
import {
  GlobalConfiguration,
  PaginationConfiguration,
} from "../../../utils/configurationProvider.js";
import FormatAmount from "../../../components/_utils/FormatAmount/FormatAmount.js";
import formatDateViewAll from "../../../utils/formatDateViewAll.js";
import ICONS from "../../../assets/Icons/index.js";

const PrivateTxnValidationRequireApproval = ({ typepop }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const filterRef = useRef(null);
  const labelActionDispatchedRef = useRef(false);

  const [pageSize, setPageSize] = useState(
    PaginationConfiguration.paginationSmall,
  );
  const [moreDetailId, setMoreDetailId] = useState("");
  const [moreDetailOpen, setMoreDetailOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(
    GlobalConfiguration.lengthCheckOne,
  );
  const [currentCategory, setCurrentCategory] = useState("");
  const [active, setActive] = useState("");
  const [inputData, setInputData] = useState([]);
  const [LabelSearchText, setLabelSearchText] = useState("");

  const [searchUserTextDebounce, setSearchUserTextDebounce] = useState("");

  const [filterOpen, setFilterOpen] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showInnerSearchBar, setShowInnerSearchBar] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [isLabelPopupOpen, setIsLabelPopupOpen] = useState(false);
  const [labelId, setLabelId] = useState("");
  const [labels, setLabels] = useState([]);
  const [phantomMarker, setPhantomMarker] = useState(
    GlobalConfiguration.lengthCheckZero,
  );

  const [privateTxnData, setPrivateTxnData] = useState({
    transaction_requests: [],
    total_count: GlobalConfiguration.lengthCheckZero,
    title: {
      records: GlobalConfiguration.lengthCheckZero,
      total_pages: GlobalConfiguration.lengthCheckZero,
      page_item_count: pageSize,
    },
  });

  const [allPrivateTxnDataLoading, setAllPrivateTxnDataLoading] =
    useState(true);

  const { markerData } = useSelector((s) => s.multiEntitySwitcherSlice);
  const { ownerAccBalanceLoading } = useSelector(
    (state) => state.ownerAllVerification,
  );

  const ensureSession = async () => {
    return true;
  };

  const getPrivateTxnData = async () => {
    try {
      setAllPrivateTxnDataLoading(true);

      const queryParams = {
        page_no: currentPage,
        page_size: pageSize,
        status: "created",
      };

      if (LabelSearchText) {
        queryParams.labels = LabelSearchText;
      }
      if (currentCategory) {
        queryParams.category = currentCategory;
      }

      const response = await dispatch(getAllPrivateTransactions(queryParams));
      const rawData = response?.payload || {};

      setPrivateTxnData(rawData);
      setLabels(rawData?.summary?.available_labels ?? []);
    } catch (error) {
      setPrivateTxnData({});
    } finally {
      setAllPrivateTxnDataLoading(false);
    }
  };

  const handlePagination = (page) => setCurrentPage(page);

  const handleSelect = (value) => {
    setPageSize(value);
    setCurrentPage(GlobalConfiguration.lengthCheckOne);
  };

  const handleSearch = (text) => {
    setSearchUserTextDebounce(text);
  };

  const handleRefreshBalance = async (id) => {
    if (!id) return;
    try {
      const response = await dispatch(getOwnerAccBalance(id));

      if (response?.payload?.status === true) {
        const newBalance =
          response.payload.owner_accounts[GlobalConfiguration.lengthCheckZero]
            ?.owner_account_balance;
        const updatedTime =
          response.payload.owner_accounts[GlobalConfiguration.lengthCheckZero]
            ?.owner_account_balance_last_fetched_at_in_words;

        setPrivateTxnData((prev) => ({
          ...prev,
          transaction_requests: prev.transaction_requests.map((txn) =>
            txn?.owner_account_details?.owner_account_id === id
              ? {
                  ...txn,
                  owner_account_details: {
                    ...txn.owner_account_details,
                    owner_account_balance: newBalance,
                    owner_account_balance_last_fetched_at_in_words: updatedTime,
                  },
                }
              : txn,
          ),
        }));
        toast.dismiss();

        CommonToast({
          text: "Balance updated",
          icon: "https://q2pay.in/new_assets/common/approval/select_all_with_white_filled_circle.svg",
          variant: "var(--success)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
      } else {
        toast.dismiss();
        CommonToast({
          text:
            response?.payload?.response?.data?.errors?.base[
              GlobalConfiguration.lengthCheckZero
            ] || "Update failed",
          icon: ICONS?.ERROR_WHITE_CIRCLE,
          variant: "var(--error)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
      }
    } catch (err) {
      toast.dismiss();
      CommonToast({
        text: "Error updating balance",
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });
    }
  };

  const handleLabelPopupClose = () => {
    setIsLabelPopupOpen(false);
    setPhantomMarker((x) => x + GlobalConfiguration.lengthCheckOne);
  };

  const handleClearParams = () => {
    setLabelSearchText("");
    setCurrentCategory("");
    setSearchUserTextDebounce("");
    setInputData([]);
    setCurrentPage(GlobalConfiguration.lengthCheckOne);
    setPageSize(
      PaginationConfiguration.paginationSmall ||
        PaginationConfiguration.paginationSmall,
    );
    setShowInnerSearchBar(false);
  };

  const openMoreDetailPopup = () => setMoreDetailOpen(true);

  const getErrorMessage = (res) => {
    const p =
      res?.payload.response.data.ids?.unsuccessful_ids[0].errors ||
      res?.payload;
    const e = res?.error;
    if (typeof p === "string") return p;
    if (p?.message) return p.message;
    if (p?.errors?.base?.[0]) {
      const base = p.errors.base[0];
      return typeof base === "string"
        ? base
        : base?.errors?.[0] || base?.message || "Something went wrong";
    }
    if (e?.message) return e.message;
    return "Something went wrong";
  };

  const handleAssignLabelsProp = async (labelsToAssign) => {
    setAllPrivateTxnDataLoading(true);

    const toArray = (value) => (Array?.isArray(value) ? value : [value]);
    const recordIds = toArray(labelId);
    console.log(recordIds, labelsToAssign, "recordIds, labelsToAssign");

    const res = await dispatch(
      addNewLabelPvt({ record_ids: recordIds, labels: labelsToAssign }),
    );
    if (addNewLabelPvt.fulfilled.match(res)) {
      await getPrivateTxnData();
      if (labelActionDispatchedRef) labelActionDispatchedRef.current = true;
    }
    if (addNewLabelPvt.rejected.match(res)) {
      CommonToast({
        text: getErrorMessage(res),
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text_primary)",
          enabledBgColor: "var(--primary_bg)",
        },
      });
    }
    setAllPrivateTxnDataLoading(false);
  };

  const handleUnassignLabelsProp = async (labelsToUnassign) => {
    setAllPrivateTxnDataLoading(true);

    const toArray = (value) => (Array.isArray(value) ? value : [value]);
    const recordIds = toArray(labelId);
    const res = await dispatch(
      removeNewLabelPvt({
        record_ids: recordIds,
        labels: labelsToUnassign,
      }),
    );
    if (removeNewLabelPvt.fulfilled.match(res)) {
      await getPrivateTxnData();
      if (labelActionDispatchedRef) labelActionDispatchedRef.current = true;
    }
    if (removeNewLabelPvt.rejected.match(res)) {
      CommonToast({
        text: getErrorMessage(res),
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text_primary)",
          enabledBgColor: "var(--primary_bg)",
        },
      });
    }
    setAllPrivateTxnDataLoading(false);
    if (removeNewLabelPvt?.fulfilled?.match(res)) {
      return true;
    } else {
      return false;
    }
  };

  const handleMount = async () => {};

  useEffect(() => {
    dispatch(enableSwitcher());
    handleMount();

    return () => dispatch(disableSwitcher());
  }, [markerData]);

  useEffect(() => {
    if (isLabelPopupOpen || moreDetailOpen) dispatch(disableSwitcher());
    else dispatch(enableSwitcher());
  }, [isLabelPopupOpen, moreDetailOpen]);

  useEffect(() => {
    if (!isLabelPopupOpen) return;
    const availableLabels = sessionStorage?.getItem("AvailableLabelsPvt");
    const parsedLabels = JSON?.parse(availableLabels) || [];
    setLabels(parsedLabels);
  }, [isLabelPopupOpen]);

  useEffect(() => {
    if (ownerAccBalanceLoading) {
      CommonToast({
        text: "Refreshing balance",
        icon: "https://q2pay.in/new_assets/gifs/inputloader_white.gif",
        variant: "var(--processing)",
        buttonNeeded: false,
        btnProps: {
          enabledBgColor: "var(--primary-bg)",
          enabledTextColor: "var(--text-primary)",
          btnText: "",
          handleFunction: () => {},
          icon: "",
          btnType: "button",
          isDisabled: false,
          hoverOption: "white",
        },
      });
    }
  }, [ownerAccBalanceLoading]);

  useEffect(() => {
    getPrivateTxnData();
  }, [
    markerData,
    currentPage,
    pageSize,
    LabelSearchText,
    currentCategory,
    phantomMarker,
    moreDetailOpen,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      let response;
      let data = [];

      try {
        switch (active) {
          case "Labels":
            data = labels
              ?.filter((itm) => {
                if (itm.includes(searchUserTextDebounce)) return itm;
              })
              ?.map((val) => {
                return { id: val, Label: val };
              });
            setInputData(data);
            break;

          case "Category":
            response = await dispatch(
              beneCategories({ search_string: searchUserTextDebounce }),
            );
            data =
              response?.payload?.categories?.map((item) => ({
                id: item?.name,
                Category_name: item?.name,
              })) || [];
            break;

          default:
            data = [];
            break;
        }
      } catch (error) {
        data = [];
      } finally {
        setInputData(data);
        // setLoadingData(false);
      }
    };
    fetchData();
  }, [active]);
  return (
    <>
      {moreDetailOpen && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <PrivateDetailPopup
            closePrivateDetailModal={() => {
              setMoreDetailOpen(false);
            }}
            private_id={moreDetailId}
            type={"private_detail_modal"}
          />
        </div>
      )}
      {isLabelPopupOpen && (
        <div
          onClick={handleLabelPopupClose}
          className="fixed left-left-base top-top-base w-full h-full bg-PopUp-BackDrop bg-opacity-dark-opacity flex items-center justify-center z-z-index-modal"
        >
          <CommonAssignLabelPopup
            headerTitle="Assign/unassign labels:"
            headersubtext="Labels will be used to generate statistics for the associated transactions."
            closehandleFunction={handleLabelPopupClose}
            closeisDisabled={false}
            labels={labels}
            setLabels={setLabels}
            LabelAccounts={labelId}
            isLabelPopupOpen={isLabelPopupOpen}
            filteredData={(privateTxnData?.transaction_requests || [])
              .filter((acc) => {
                const currentId = acc?.request_reference_id;

                return Array.isArray(labelId)
                  ? labelId.includes(currentId)
                  : labelId === currentId;
              })
              .map((acc) => ({
                ...acc,
                id: acc?.request_reference_id,
              }))}
            onClose={handleLabelPopupClose}
            loading={allPrivateTxnDataLoading}
            setLoading={setAllPrivateTxnDataLoading}
            handleAssignLabels={handleAssignLabelsProp}
            handleUnassignLabels={handleUnassignLabelsProp}
            type="viewall"
          />
        </div>
      )}

      <div
        className={`flex flex-col items-center relative w-width-full h-full-height overflow-hidden`}
      >
        <div
          className={`bene_div_container z-z-index-dropdown Qmd:shadow-box-shadow-bottom Qmd:bg-primary-nav bg-primary-nav-act ${typepop === "popup" ? " w-full Qmd:w-full-popup-width" : "w-full-width"} relative `}
        >
          <div className="Qmd:min-h-navbar-breadcrumb-height bg-primary-nav"></div>
          <TopNavComponent
            topNavCtaBtn1Needed={false}
            handleBack={
              typepop === "popup"
                ? undefined
                : () => navigate("/private_transaction_dashboard")
            }
            filteredSearchProps={{
              isSearchDisabled: allPrivateTxnDataLoading,
              setShowInnerSearchBar,
              showSearchBar,
              onSearchInput: handleSearch,
              searchText: searchUserTextDebounce,
              searchPlaceholder: "Search private transactions",
              filterPlaceholder: "transaction details",
              filterItems: [
                {
                  name: "Category",
                  short_name: "Cat",
                  valueType: "string",
                  isMulti: false,
                  value: currentCategory,
                  setValue: setCurrentCategory,
                  isDropdown: true,
                  isDisabled: allPrivateTxnDataLoading,
                },
                {
                  name: "Labels",
                  short_name: "Lab",
                  valueType: "string",
                  isMulti: true,
                  value: LabelSearchText,
                  setValue: setLabelSearchText,
                  isDropdown: true,
                  isDisabled: allPrivateTxnDataLoading,
                },
              ],
              inputData: inputData,
              setActive: setActive,
              isShowSortBy: false,
            }}
          />
        </div>
        <div
          style={{
            backgroundColor: showInnerSearchBar ? "var(--PopUp-BackDrop)" : "",
          }}
          className={`flex flex-col ${showInnerSearchBar ? "opacity-dark-opacity" : ""} items-center w-width-full h-height-full bg-primary-bg  overflow-auto ${privateTxnData?.transaction_requests?.length > GlobalConfiguration.lengthCheckZero ? "pb-padding-lg" : ""} `}
        >
          {allPrivateTxnDataLoading ? (
            <div
              className={`m-auto justify-center items-center flex ${
                showInnerSearchBar
                  ? "overflow-y-hidden h-screen w-screen  opacity-light-opacity bg-PopUp-BackDrop cursor-pointer-none pointer-events-none"
                  : ""
              }`}
            >
              <Loader />
            </div>
          ) : privateTxnData?.transaction_requests &&
            privateTxnData?.transaction_requests.length >
              GlobalConfiguration.lengthCheckZero &&
            !allPrivateTxnDataLoading ? (
            <div
              className={`scroll flex flex-col  w-width-full ${
                showInnerSearchBar
                  ? "overflow-y-hidden h-screen  opacity-light-opacity bg-PopUp-BackDrop cursor-pointer-none pointer-events-none justify-start items-center"
                  : "items-center justify-center"
              }`}
              ref={filterRef}
            >
              <div
                className={`scroll z-z-index-base flex flex-col ${showInnerSearchBar ? "opacity-light-opacity" : ""} items-center justify-center w-width-full lg:w-auto px-padding-md lg:px-padding-base pb-padding-xl gap-gap-md`}
              >
                {privateTxnData?.transaction_requests.map((txn, index) => {
                  return (
                    <div
                      key={txn?.private_transaction_id}
                      className={`w-width-full ${index === GlobalConfiguration.lengthCheckZero ? "mt-margin-md" : ""}`}
                    >
                      <CommonAccountApprovalCard
                        entries={[
                          [
                            {
                              type: "textSubtext",
                              text:
                                "INR " +
                                FormatAmount({
                                  price: txn?.total_amount,
                                }),
                              textColor: "settled",
                              subText: "Total transaction amount",
                              isBold: true,
                            },
                            {
                              type: "accountNumber",
                              text: txn.owner_account_details
                                ?.owner_account_bank_acc_no,
                              subText: "Source account",
                              ifsc: txn.owner_account_details
                                ?.owner_account_ifsc_code,
                            },
                            {
                              type: "amount",
                              text:
                                txn.owner_account_details
                                  ?.owner_account_balance ||
                                GlobalConfiguration.lengthCheckZero,
                              subText: txn.owner_account_details
                                ?.owner_account_balance_last_fetched_at_in_words
                                ? `Balance  ${"-" + " " + txn.owner_account_details.owner_account_balance_last_fetched_at_in_words}`
                                : "Balance",
                              refreshOnClick: () =>
                                handleRefreshBalance(
                                  txn.owner_account_details?.owner_account_id,
                                ),
                            },
                          ],
                          [
                            {
                              type: "textSubtextX2",
                              text: txn.category || "—",
                              subText: "Category",
                              text1: txn.created_at
                                ? formatDateViewAll(txn.created_at, true, true)
                                : "—",
                              subText1: "Created on",
                            },
                            {
                              type: "textSubtext",
                              text: txn.total_beneficiary_account_count,
                              subText: "Total beneficiary accounts",
                            },
                            {
                              type: "cta",
                              text:
                                txn.labels?.join(", ") || "No labels assigned",
                              subText: "Labels",
                              onClick: () => {
                                setLabelId(txn.request_reference_id);
                                setIsLabelPopupOpen(true);
                              },
                            },
                          ],
                          [
                            {
                              type: "textSubtext",
                              text: "Validation pending",
                              subText: "File status",
                              textColor: "var(--pending)",
                            },
                          ],
                        ]}
                        cardState="single"
                        ctaBtn2Text="View details"
                        overlay1CtaOnClick={async () => {
                          const ok = await ensureSession();
                          if (!ok) return;
                          setMoreDetailId(txn?.request_reference_id);
                          openMoreDetailPopup();
                        }}
                        overlay1Cta2OnClick={async () => {
                          const ok = await ensureSession();
                          if (!ok) return;
                          navigate(
                            `/private_transaction_dashboard/require_validation/validate_file/${txn.request_reference_id}`,
                          );
                        }}
                        overlay1Text="Require validation"
                        overlay1Subtext=" "
                        overlay1CtaText="View details"
                        overlay1Cta2Text="Validate file"
                        overlay1Cta2HoverType="black"
                        overlay1Cta2Color="var(--positive-act)"
                        overlay1Icon="https://q2pay.in/new_assets/common/approval/pending.svg"
                        overlay1IconColor="var(--pending)"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div
              className={`flex flex-col items-center justify-center m-auto  Qsmd:mt-[unset] mt-margin-md h-screen ${
                showInnerSearchBar
                  ? "overflow-y-hidden h-screen w-screen  opacity-light-opacity bg-PopUp-BackDrop cursor-pointer-none pointer-events-none"
                  : ""
              }`}
            >
              {LabelSearchText || currentCategory ? (
                <div className="flex flex-col gap-gap-sm items-center justify-center">
                  <img
                    src="https://q2pay.in/new_assets/graphics/no_data/no_data_found.svg"
                    alt="No accounts found"
                    className="w-graphic-width-lg h-graphic-square-height-md"
                  />
                  <p className="text-text-primary text-font-sm font-normal">
                    No data found.
                  </p>
                  <div>
                    <PrimaryButton
                      enabledBgColor="var(--primary-nav-act)"
                      btnText="Clear parameters"
                      btnType="button"
                      handleFunction={handleClearParams}
                      enabledTextColor="var(--text-primary)"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-gap-md items-center justify-center">
                  <img
                    src="https://q2pay.in/new_assets/graphics/no_data/no_pending_transaction.svg"
                    alt="No accounts found"
                    className="w-graphic-width-lg h-graphic-square-height-md"
                  />
                  <p className="text-text-primary text-font-sm font-normal">
                    There are no more transactions that require your approval.
                  </p>
                  <div>
                    <PrimaryButton
                      btnText="Private transaction dashboard"
                      handleFunction={() =>
                        navigate("/private_transaction_dashboard")
                      }
                      btnType="button"
                      isDisabled={false}
                      enabledTextColor="var(--text-primary)"
                      disabledTextColor="var(--text-primary)"
                      enabledBgColor="var(--primary-nav-act)"
                      disabledBgColor="var(--primary-nav-act)"
                      hoverOption="white"
                      fullWidthRequired={false}
                      inActiveVariantNeeded={false}
                      isLoaderNeeded={false}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {privateTxnData?.transaction_requests.length ==
          GlobalConfiguration.lengthCheckZero ? null : (
            <div className="fixed w-full bottom-bottom-base bg-primary-nav z-z-index-tooltip">
              <div
                className={`${filterOpen ? "opacity-light-opacity bg-PopUp-BackDrop h-height-full" : ""}`}
              >
                {showInnerSearchBar && (
                  <div className="absolute inset-top-base z-z-index-overlay bg-PopUp-BackDrop opacity-dark-opacity" />
                )}
                <PaginationComponent
                  handleSelect={handleSelect}
                  selectedValue={pageSize}
                  data={privateTxnData}
                  dropdownOptions={PaginationConfiguration?.paginationOptions}
                  handlePagination={handlePagination}
                  currentPage={currentPage}
                  setIsOpen={setIsOpen}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PrivateTxnValidationRequireApproval;
